import { useState, useCallback, useMemo, useEffect, useRef, useContext } from "react";
import {
  Paper,
  Title,
  Button,
  Group,
  Stack,
  Text,
  FileInput,
  Card,
  Divider,
  Badge,
  Box,
  Flex,
  MultiSelect,
  NumberInput,
  Grid,
  Tooltip,
  Accordion,
  Loader,
  ActionIcon,
  SegmentedControl,
  Checkbox,
  Select,
} from "@mantine/core";
import { DateInput, TimeInput } from '@mantine/dates';
import { notifications } from "@mantine/notifications";
import { IconUpload, IconSend, IconCheck, IconClock, IconCalendar } from "@tabler/icons-react";
import { useOptimizedEditor } from "../../../hooks/useOptimizedEditor";
import { RichTextEditorComponent } from "./RichTextEditor";
import axios from "axios";
import { PhoneInput } from "../../../components/PhoneInput/PhoneInput";
import { PhoneNumberItem } from "../../../components/PhoneInput/PhoneNumberItem";
import showNotification from "../../../utils/showNotification";
import moment from "moment";
import AppContext from "../../../contexts/AppContext";
import { LEADS_CAMPAIGN } from "../../../utils/leads-constant";
import { parsePhoneList } from "../../../utils/parsePhoneList";

const MODE_OPTIONS = [
  { value: "send", label: "Enviar mensajes" },
  { value: "register", label: "Solo registrar (ya los envié)" },
];

export function Messages({ form, loading }) {
  const { staffUsers, currentUser, academicUnits } = useContext(AppContext);
  const isRegister = form.values.mode === "register";

  const sellerOptions = useMemo(
    () => (staffUsers || [])
      .filter((item) => item?.roles?.includes("ventas"))
      .map((item) => ({ value: String(item.value), label: item.label })),
    [staffUsers]
  );

  // Por defecto el vendedor es el usuario logueado (si es de ventas)
  useEffect(() => {
    const currentId = currentUser?.user?._id && String(currentUser.user._id);
    if (!form.values.seller_id && currentId && sellerOptions.some((o) => o.value === currentId)) {
      form.setFieldValue("seller_id", currentId);
    }
  }, [currentUser, sellerOptions]);

  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [newPhone, setNewPhone] = useState("");
  const [evolutionInstances, setEvolutionInstances] = useState({
    isLoading: false,
    data: [],
    error: "",
  });

  const editor = useOptimizedEditor();

  // Agrega números a la lista sin repetir. Devuelve cuántos eran nuevos.
  const addPhones = useCallback((numbers) => {
    const nuevos = numbers.filter((n) => !phoneNumbers.includes(n));
    if (nuevos.length > 0) {
      setPhoneNumbers((prev) => [...prev, ...nuevos]);
      form.setFieldValue("phones", (prev) => [...prev, ...nuevos]);
    }
    return nuevos.length;
  }, [phoneNumbers]);

  // Permite pegar varios números a la vez (separados por espacio, coma, etc.)
  const handleAddPhone = useCallback(() => {
    const agregados = addPhones(parsePhoneList(newPhone));
    if (agregados > 0) {
      setNewPhone("");
      notifications.show({
        title: agregados === 1 ? "Número agregado" : "Números agregados",
        message: agregados === 1
          ? "El número ha sido agregado a la lista"
          : `${agregados} números han sido agregados a la lista`,
        color: "blue",
      });
    }
  }, [newPhone, addPhones]);

  const handleRemovePhone = useCallback((phone) => {
    const filteredPhones = phoneNumbers.filter((p) => p !== phone);
    setPhoneNumbers(filteredPhones);
    form.setFieldValue("phones", filteredPhones);
    notifications.show({
      title: "Número eliminado",
      message: "El número ha sido eliminado de la lista",
      color: "green",
    });
  }, [phoneNumbers]);

  const handleFileUpload = useCallback(
    (file) => {
      if (file) {
        const reader = new FileReader();
        reader.onload = (e) => {
          const agregados = addPhones(parsePhoneList(e.target.result));
          notifications.show({
            title: "Archivo procesado",
            message: `${agregados} números han sido agregados`,
            color: agregados > 0 ? "blue" : "orange",
          });
        };
        reader.readAsText(file);
      }
    },
    [addPhones]
  );

  const phoneNumbersList = useMemo(() => {
    return phoneNumbers.map((phone) => (
      <PhoneNumberItem
        key={phone}
        phone={phone}
        onRemove={handleRemovePhone}
      />
    ));
  }, [phoneNumbers, handleRemovePhone]);

  const handleConfirmMessage = () => {
    form.setFieldValue("message", null);
    form.setFieldValue("mediaData", null);
    const editorContent = editor.getHTML();
    if (!editor?.getText() && !editor?.getHTML().includes("img")) {
      notifications.show({
        title: "Error",
        message: "Por favor, escribe un mensaje o agrega una imagen",
        color: "red",
      });
      return;
    }
    const hasImage = editorContent.includes("img");
    let base64Image = "";
    let caption = "";

    if (hasImage) {
      // Extract base64 image and caption
      const imgMatch = editorContent.match(/<img[^>]+src="([^">]+)"/);
      if (imgMatch) {
        base64Image = imgMatch[1];
        // Get text content as WhatsApp formatted text
        caption = editor.getWhatsAppContent();
      }
    }

    const message_type = hasImage ? "media" : "text";
    form.setFieldValue("message_type", message_type);
    hasImage
      ? form.setFieldValue("mediaData", {
        mediaType: "image",
        fileName: "imagen.jpg",
        caption,
        media: base64Image,
      })
      : form.setFieldValue("message", editor.getWhatsAppContent());

    return notifications.show({
      title: "Mensaje confirmado",
      message: "Se ha establecido el contenido para la campaña",
      color: "green",
    });
  };

  useEffect(() => {
    const getEvolutionApiInstances = async () => {
      setEvolutionInstances((prev) => ({ ...prev, isLoading: true }));
      const URL = `${process.env.REACT_APP_EVOLUTION_API_URL}/instance/fetchInstances`;
      const API_KEY = process.env.REACT_APP_EVOLUTION_API_KEY;

      const options = {
        method: "GET",
        url: URL,
        headers: {
          apikey: API_KEY,
        },
      };

      axios(options)
        .then((response) => {
          const { data } = response;

          const responseData = data
            .filter((instance) => instance.connectionStatus === 'open')
            .map((instance) => {
              const { name, number } = instance;
              return {
                value: name,
                label: number ? `${name} (${number})` : name
              };
            });
          setEvolutionInstances((prev) => ({
            ...prev,
            data: responseData,
          }));
          form.setFieldValue("api_instances", responseData.map(item => item.value));
        })
        .catch((error) => {
          setEvolutionInstances(prev => ({
            ...prev,
            error: error.message
          }))
          showNotification({
            color: "red",
            status: "error",
            title: "Ha ocurrido un error",
            autoClose: 6000,
            message: `No se han cargado bien los bots. Por favor intente nuevamente. Detalle: ${error.message}`,
          });
        })
        .finally(() => {
          setEvolutionInstances((prev) => ({
            ...prev,
            isLoading: false,
          }));
        });
    };
    getEvolutionApiInstances();
  }, []);

  const time_picker_ref = useRef(null);
  const pickerControl = (
    <ActionIcon variant="subtle" color="gray" onClick={() => time_picker_ref.current?.showPicker()}>
      <IconClock size={16} stroke={1.5} />
    </ActionIcon>
  );


  return (
    <section style={{ width: "100%" }}>
      <Stack spacing="xl">
        <Card withBorder shadow="sm" radius="md">
          <Stack gap="xs">
            <SegmentedControl
              fullWidth
              data={MODE_OPTIONS}
              value={form.values.mode}
              onChange={(value) => form.setFieldValue("mode", value)}
            />
            <Text c="dimmed" size="sm">
              {isRegister
                ? "No se envía ningún mensaje. Solo se carga la interacción por WhatsApp en el lead de cada número (si no existe, se crea)."
                : "Los mensajes se envían por los bots y además se carga la interacción en el lead de cada número."}
            </Text>
          </Stack>
        </Card>

        {isRegister && (
          <Card withBorder shadow="sm" radius="md">
            <Stack spacing="md">
              <Title order={2} style={{ color: "var(--color-text-primary)" }}>
                Consultó por
              </Title>
              <Divider my="sm" />
              <MultiSelect
                label="Unidades académicas"
                description="Se guardan en la interacción de cada lead"
                placeholder="Buscar unidad académica"
                searchable
                clearable
                withAsterisk
                data={academicUnits || []}
                {...form.getInputProps("academic_units")}
              />
              <Checkbox
                label="Omitir leads que ya tienen esta unidad académica"
                description="Si el lead ya tiene una interacción con alguna de las unidades elegidas, no se le vuelve a cargar esa unidad. Si las tiene todas, se omite."
                {...form.getInputProps("skip_existing", { type: "checkbox" })}
              />
            </Stack>
          </Card>
        )}

        {!isRegister && (
        <Card
          withBorder
          shadow="sm"
          radius="md"
          style={{ backgroundColor: "var(--color-card-background)" }}
        >
          <Stack spacing="md">
            <Title
              order={2}
              style={{ color: "var(--color-text-primary)" }}
            >
              Envío de Mensajes
            </Title>

            <Divider my="sm" />

            <RichTextEditorComponent editor={editor} />

            <Button
              type="button"
              variant="outline"
              w="max-content"
              onClick={handleConfirmMessage}
            >
              {form.values?.message !== null ||
                form?.values?.mediaData !== null ? (
                <Flex align="center" gap={8}>
                  <IconCheck />
                  <span>Confirmado</span>
                </Flex>
              ) : (
                "Confirmar mensaje"
              )}
            </Button>
          </Stack>
        </Card>
        )}

        <Card withBorder shadow="sm" radius="md">
          <Stack spacing="md">
            <Title order={2} style={{ color: "var(--color-text-primary)" }}>
              Registro en leads
            </Title>
            <Divider my="sm" />
            <Grid>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <Select
                  label="Vendedor a cargo"
                  searchable
                  withAsterisk
                  data={sellerOptions}
                  {...form.getInputProps("seller_id")}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <Select
                  label="Forma de contacto"
                  withAsterisk
                  data={LEADS_CAMPAIGN}
                  {...form.getInputProps("campaign")}
                />
              </Grid.Col>
            </Grid>
            <Text c="dimmed" size="sm">
              Canal: WhatsApp · Resultado: Pendiente
            </Text>
          </Stack>
        </Card>

        <Card withBorder shadow="sm" radius="md">
          <Stack spacing="md">
            <Title
              order={2}
              style={{ color: "var(--color-text-primary)" }}
            >
              Destinatarios
            </Title>
            <Text c="dimmed">
              Agrega los números de teléfono de los destinatarios
            </Text>

            <Divider my="sm" />

            <PhoneInput
              value={newPhone}
              onChange={(event) =>
                setNewPhone(event.target.value)
              }
              onAdd={handleAddPhone}
            />

            <FileInput
              label="O cargar desde CSV"
              placeholder="Seleccionar archivo"
              accept=".csv,.txt"
              onChange={handleFileUpload}
              leftSection={<IconUpload size={14} />}
              mb="md"
            />

            <Paper
              withBorder
              p="md"
              radius="md"
              style={{
                backgroundColor: "var(--color-card-background)",
              }}
            >
              <Group justify="space-between" mb="md">
                <Text fw={500}>Números agregados</Text>
                <Badge size="lg" variant="light" color="blue">
                  {phoneNumbers.length}
                </Badge>
              </Group>

              <Box
                style={{
                  maxHeight: "300px",
                  overflowY: "auto",
                }}
              >
                {phoneNumbersList}
              </Box>
            </Paper>
          </Stack>
        </Card>

        {!isRegister && (
        <Card withBorder shadow="sm" radius="md">
          <Accordion>
            <Accordion.Item value="configuracion">
              <Accordion.Control>
                <Title
                  order={2}
                  style={{
                    color: "var(--color-text-primary)",
                  }}
                >
                  Configuración avanzada
                </Title>
              </Accordion.Control>
              <Accordion.Panel>
                <Stack spacing="md">

                  <Grid>
                    <Grid.Col span={{ base: 12, sm: 6 }}>
                      
                      <DateInput
                      {...form.getInputProps('date')}
                        label="Programar fecha"
                        leftSection={<IconCalendar />}
                        valueFormat="DD/MM/YYYY"
                        value={form.values.date ? moment(form.values.date, 'DD/MM/YYYY').toDate() : null}
                        onChange={(date) => {
                          
                          if (!date) {
                            form.setFieldValue('date', '');
                            return;
                          }

                          // Convertimos el objeto Date que entrega Mantine a formato "DD/MM/YYYY"
                          const formatted = moment(date).format('DD/MM/YYYY');
                          form.setFieldValue('date', formatted);
                        }}
                      />
                    </Grid.Col>
                    <Grid.Col span={{ base: 12, sm: 6 }}>
                      <TimeInput
                        onChange={(event) => form.setFieldValue('time', event.currentTarget.value)}
                        {...form.getInputProps('time')}
                        label="Programar hora"
                        ref={time_picker_ref}
                        leftSection={pickerControl} />
                    </Grid.Col>
                  </Grid>

                  {evolutionInstances.isLoading && (
                    <Loader size="sm" />
                  )}
                  {evolutionInstances.error && (
                    <Stack gap={0}>
                      <Text
                        style={{
                          fontSize: "var(--fs-body)",
                        }}
                      >
                        Bots a utilizar:
                      </Text>
                      <Badge color="red">
                        Error:{" "}
                        {evolutionInstances.error}
                      </Badge>
                      <p
                        style={{
                          fontSize: "var(--fs-tiny)",
                          color: "var(--color-text-primary)",
                        }}
                      >
                        Por favor, racargue la página e
                        intente nuevamente
                      </p>
                    </Stack>
                  )}
                  {evolutionInstances?.data &&
                    evolutionInstances?.data?.length >
                    0 && (
                      <MultiSelect
                        data={evolutionInstances?.data}
                        label="Bots a utilizar"
                        description="A cada bot le tocará un número proporcional en relación a la cantidad de mensajes"
                        {...form.getInputProps(
                          "api_instances"
                        )}
                      />
                    )}

                  <NumberInput
                    label="Bloque de mensajes"
                    description="Cantidad de mensajes que se realizan por bloque antes de una pausa"
                    {...form.getInputProps("batch_size")}
                  />

                  <NumberInput
                    label="Tiempo de pausa entre bloques"
                    description="Expresado en minutos"
                    {...form.getInputProps("pause")}
                  />

                  <Grid>
                    <Grid.Col
                      span={12}
                      p={0}
                      mb={0}
                      mt={4}
                      pl={8}
                    >
                      <Title
                        order={6}
                        style={{
                          color: "var(--color-text-primary)",
                        }}
                      >
                        Intervalo de tiempo entre
                        mensajes
                      </Title>
                    </Grid.Col>
                    <Grid.Col span={{ base: 12, sm: 6 }}>
                      <Tooltip label="Ejemplo: 1 segundo = 1000 ms">
                        <NumberInput
                          label="Tiempo mínimo"
                          description="Expresado en milisegundos"
                          {...form.getInputProps(
                            "interval_range.min_ms"
                          )}
                        />
                      </Tooltip>
                    </Grid.Col>
                    <Grid.Col span={{ base: 12, sm: 6 }}>
                      <Tooltip label="Ejemplo 5 segundos = 5000 ms">
                        <NumberInput
                          label="Tiempo máximo"
                          description="Expresado en milisegundos"
                          {...form.getInputProps(
                            "interval_range.max_ms"
                          )}
                        />
                      </Tooltip>
                    </Grid.Col>
                  </Grid>
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion>
        </Card>
        )}
        <Button
          fullWidth
          type="submit"
          size="lg"
          loading={loading}
          disabled={
            !isRegister && (
              evolutionInstances.isLoading ||
              !!evolutionInstances.error ||
              evolutionInstances?.data?.length === 0
            )
          }
          leftSection={isRegister ? <IconCheck size={20} /> : <IconSend size={20} />}
        >
          {isRegister ? `Registrar interacciones (${phoneNumbers.length})` : "Enviar Mensajes"}
        </Button>
      </Stack>
    </section>
  );
}
